'use client';

import { useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  IconButton as MuiIconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@mui/material';

const DEFAULT_SX = {
  '& .MuiOutlinedInput-root': { bgcolor: 'background.default' },
  '& .MuiFormHelperText-root': { fontSize: '0.75rem' },
} as const;

export interface InputWithControllerProps<T extends FieldValues> extends Omit<
  TextFieldProps,
  'name' | 'value' | 'onChange' | 'onBlur' | 'ref'
> {
  control: Control<T>;
  name: FieldPath<T>;
  showPasswordToggle?: boolean;
}

export function InputWithController<T extends FieldValues>({
  control,
  name,
  showPasswordToggle = false,
  type = 'text',
  sx,
  InputProps,
  ...props
}: InputWithControllerProps<T>) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const shouldShowToggle = isPassword && showPasswordToggle;
  const inputType = shouldShowToggle ? (showPassword ? 'text' : 'password') : type;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          {...props}
          type={inputType}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          inputProps={{
            ...props.inputProps,
            'aria-required': props.required ?? undefined,
            'aria-invalid': !!fieldState.error,
          }}
          InputProps={{
            ...InputProps,
            ...(shouldShowToggle && {
              endAdornment: (
                <InputAdornment position="end">
                  <MuiIconButton
                    aria-label={showPassword ? 'hide password' : 'show password'}
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </MuiIconButton>
                </InputAdornment>
              ),
            }),
          }}
          sx={{ ...DEFAULT_SX, ...sx }}
        />
      )}
    />
  );
}

export default InputWithController;
